import { OrderGuideMergeStrategy } from '@/enums';

export interface OrderGuideEditabilityState {
  isDefault: boolean;
  linkedSupplierCollectionUUID: string | null;
}

export interface ChatEditabilityConfig {
  chatUUID: string | null;
  customOrderGuideEditingEnabled: boolean;
  orderGuideMergeStrategy: OrderGuideMergeStrategy;
}

/**
 * A guide's editability:
 * - `editable` — the rep can edit it.
 * - `allGuidesLocked` — every guide for the customer is locked (merge strategy
 *   `DISTRIBUTOR_ONLY`, editing disabled, or catalog-limited).
 * - `defaultGuideLocked` — only this supplier-synced default list is locked, the customer's other guides stay editable.
 */
export type OrderGuideEditability =
  | 'editable'
  | 'allGuidesLocked'
  | 'defaultGuideLocked';

interface GetOrderGuideEditabilityParams {
  customOrderGuideEditingEnabled: boolean;
  isCatalogLimited: boolean;
  orderGuide: OrderGuideEditabilityState | null;
  orderGuideMergeStrategy: OrderGuideMergeStrategy;
}

/**
 * Whether a guide is the supplier-synced default list under
 * `DISTRIBUTOR_LOCKED`. Locked guides render their default group only,
 * read-only; other guides for the same customer stay editable.
 *
 * @param orderGuide - The guide's default/linked signals
 * @param orderGuideMergeStrategy - The customer's effective merge strategy
 * @returns True when the guide is locked
 */
export const isOrderGuideDistributorLocked = (
  orderGuide: Pick<
    OrderGuideEditabilityState,
    'isDefault' | 'linkedSupplierCollectionUUID'
  >,
  orderGuideMergeStrategy: OrderGuideMergeStrategy,
): boolean =>
  orderGuide.isDefault &&
  !!orderGuide.linkedSupplierCollectionUUID &&
  orderGuideMergeStrategy === OrderGuideMergeStrategy.DISTRIBUTOR_LOCKED;

/**
 * get a guide's editability. `allGuidesLocked` when the chat's editing flag
 * is off, the merge strategy is `DISTRIBUTOR_ONLY`, or the app is catalog-only;
 *
 * `defaultGuideLocked` when only this guide is the supplier-synced locked default;
 * otherwise `editable`.
 *
 * @param customOrderGuideEditingEnabled - The chat's editing flag
 * @param isCatalogLimited - Catalog-only app; disables all editing
 * @param orderGuide - The selected guide; null when none is selected
 * @param orderGuideMergeStrategy - The customer's effective merge strategy
 * @returns The guide's editability
 */
export const getOrderGuideEditability = ({
  customOrderGuideEditingEnabled,
  isCatalogLimited,
  orderGuide,
  orderGuideMergeStrategy,
}: GetOrderGuideEditabilityParams): OrderGuideEditability => {
  const customerCanEdit =
    customOrderGuideEditingEnabled &&
    orderGuideMergeStrategy !== OrderGuideMergeStrategy.DISTRIBUTOR_ONLY &&
    !isCatalogLimited;

  if (!customerCanEdit) {
    return 'allGuidesLocked';
  }

  const isDistributorLocked =
    !!orderGuide &&
    isOrderGuideDistributorLocked(orderGuide, orderGuideMergeStrategy);

  return isDistributorLocked ? 'defaultGuideLocked' : 'editable';
};
